// Teleconsulta (telemedicina). Centro Medico → Clínica → Teleconsulta.
// La videollamada corre en la SALA PROPIA (LiveKit self-hosted, VideoSala.tsx,
// script 389): el paciente hace check-in desde el portal, el médico lo ADMITE
// y ambos entran a la sala WebRTC. El chat vive en la BD (polling).
// Consentimiento, inicio/fin, nota médica y receta al instante.
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, FileSignature, Loader2, Pill, Send, Star, Video } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import {
  Campo, CONSENT_ITEMS, ESTADO_TELE, Modal, TeleDetalle, TeleMensaje, TeleWorklistRow,
  fieldCls, hhmm, isoDate, medRpc,
} from './shared';
import { NuevaEvolucion } from './HistoriaClinica';
import { NuevaReceta } from './Recetas';
import { VideoSala } from './VideoSala';
import { EncuestaTele, IndicadoresTele, PagoBadge, PagoStaff } from './TelePago';
import { LOCALE } from '@/lib/utils/regional';

export function Teleconsulta() {
  const [tab, setTab] = useState<'agenda' | 'kpis'>('agenda');
  const [fecha, setFecha] = useState(isoDate(new Date()));
  const { data: rows = [], isFetching } = useQuery<TeleWorklistRow[]>({
    queryKey: ['med-teleconsultas', fecha], queryFn: () => medRpc<TeleWorklistRow[]>('med_teleconsultas', { _fecha: fecha }),
    refetchInterval: 10000,
  });
  const [salaCita, setSalaCita] = useState<string | null>(null);

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold flex items-center gap-2"><Video className="w-5 h-5" /> Teleconsulta</h1>
          <div className="flex rounded-md border p-0.5">
            <button type="button" onClick={() => setTab('agenda')}
              className={`rounded px-3 py-1 text-sm ${tab === 'agenda' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>Agenda</button>
            <button type="button" onClick={() => setTab('kpis')}
              className={`rounded px-3 py-1 text-sm inline-flex items-center gap-1 ${tab === 'kpis' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
              <BarChart3 className="w-3.5 h-3.5" /> Indicadores</button>
          </div>
        </div>
        {tab === 'agenda' && <input type="date" className={`${fieldCls} w-44`} value={fecha} onChange={(e) => setFecha(e.target.value)} />}
      </div>

      {tab === 'kpis' && <IndicadoresTele />}
      {tab === 'agenda' && (<>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Hora</th><th className="py-2 pr-3">Paciente</th><th className="py-2 pr-3">Médico</th>
              <th className="py-2 pr-3">Motivo</th><th className="py-2 pr-3">Consent.</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin teleconsultas para la fecha</td></tr>}
              {rows.map((r) => {
                const e = ESTADO_TELE[r.estado] ?? ESTADO_TELE.programada;
                return (
                  <tr key={r.cita_id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 font-medium">{hhmm(r.hora_inicio)}</td>
                    <td className="py-2 pr-3">{r.paciente}</td>
                    <td className="py-2 pr-3">{r.medico}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{r.motivo || '—'}</td>
                    <td className="py-2 pr-3">{r.consentimiento_aceptado ? '✓' : '—'}</td>
                    <td className="py-2 pr-3">
                      <span className={`rounded px-2 py-0.5 text-xs ${e.cls}`}>{e.label}</span>
                      {r.checkin_at && !r.admitido_at && r.estado !== 'finalizada' && (
                        <span className="ml-1 animate-pulse rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">En espera</span>
                      )}
                      <span className="ml-1"><PagoBadge estado={r.pago_estado} tarifa={r.tarifa} /></span>
                    </td>
                    <td className="py-2 text-right"><Button size="sm" onClick={() => setSalaCita(r.cita_id)}><Video className="w-4 h-4 mr-1" /> Atender</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      </>)}

      {salaCita && <Sala citaId={salaCita} onClose={() => setSalaCita(null)} />}
    </div>
  );
}

// ---- Sala del médico ----
function Sala({ citaId, onClose }: { citaId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isFetching } = useQuery<TeleDetalle>({
    queryKey: ['med-tele-cita', citaId], queryFn: () => medRpc<TeleDetalle>('med_teleconsulta_de_cita', { _cita_id: citaId }),
    refetchInterval: 8000,
  });
  const [nota, setNota] = useState(false);
  const [receta, setReceta] = useState(false);
  const [calidad, setCalidad] = useState('buena');
  const [resultado, setResultado] = useState('efectiva');
  const [video, setVideo] = useState(false);
  const [encuesta, setEncuesta] = useState(false);
  const tc = data?.teleconsulta;
  const refetch = () => { void qc.invalidateQueries({ queryKey: ['med-tele-cita', citaId] }); void qc.invalidateQueries({ queryKey: ['med-teleconsultas'] }); };

  const renovar = useMutation({
    mutationFn: () => medRpc('med_tele_generar_link', { _id: tc!.id, _horas: 24 }),
    onSuccess: () => { toast.success('Ventana de acceso renovada'); refetch(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const admitir = useMutation({
    mutationFn: () => medRpc('med_tele_admitir', { _id: tc!.id }),
    onSuccess: () => { toast.success('Paciente admitido — ya puede entrar a la sala'); refetch(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const iniciar = useMutation({ mutationFn: () => medRpc('med_tele_iniciar', { _id: tc!.id }), onSuccess: () => { toast.success('Sesión iniciada'); refetch(); }, onError: (e) => toast.error(getErrorMessage(e)) });
  const finalizar = useMutation({
    mutationFn: (calidad: string) => {
      const dur = tc?.iniciada_at ? Math.round((Date.now() - new Date(tc.iniciada_at).getTime()) / 1000) : null;
      return medRpc('med_tele_finalizar', { _id: tc!.id, _duracion_seg: dur, _calidad: calidad, _resultado: resultado });
    },
    onSuccess: () => { toast.success('Teleconsulta finalizada'); refetch(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const evento = useMutation({ mutationFn: (calidad: string) => medRpc('med_tele_evento', { _id: tc!.id, _tipo: 'calidad', _calidad: calidad }), onSuccess: () => toast.success('Calidad registrada'), onError: (e) => toast.error(getErrorMessage(e)) });

  return (
    <Modal title="Sala de teleconsulta" onClose={onClose} wide>
      {video && tc ? (
        <VideoSala teleconsultaId={tc.id} actor="medico" onCerrar={() => { setVideo(false); refetch(); }}
          onConectado={() => { if (tc.estado !== 'en_curso') iniciar.mutate(); }} />
      ) : isFetching || !tc || !data ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div> : (
        <div className="grid gap-4 md:grid-cols-2">
          {/* Columna izquierda: control */}
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">{data.paciente} <span className="text-muted-foreground">({data.paciente_ident ?? 's/i'})</span></div>
                <div className="text-xs text-muted-foreground">{data.medico} · {data.cita_fecha} {hhmm(data.cita_hora)}</div>
              </div>
              <span className={`rounded px-2 py-0.5 text-xs ${(ESTADO_TELE[tc.estado] ?? ESTADO_TELE.programada).cls}`}>{(ESTADO_TELE[tc.estado] ?? ESTADO_TELE.programada).label}</span>
            </div>

            <div className={`rounded-md border px-3 py-2 text-xs ${tc.consentimiento_aceptado ? 'bg-estable-soft text-estable border-estable/30' : 'bg-pendiente-soft text-pendiente border-pendiente/30'}`}>
              {tc.consentimiento_aceptado ? '✓ Consentimiento informado aceptado por el paciente' : '⚠ El paciente aún no acepta el consentimiento informado'}
            </div>

            <PagoStaff citaId={citaId} onCambio={refetch} />

            {/* Sala de espera virtual */}
            {tc.checkin_at && !tc.admitido_at && tc.estado !== 'finalizada' && (
              <div className="flex items-center justify-between rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                <span className="animate-pulse font-medium">🕓 El paciente está en la sala de espera (desde {new Date(tc.checkin_at).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' })})</span>
                <Button size="sm" isLoading={admitir.isPending} onClick={() => admitir.mutate()}>Admitir</Button>
              </div>
            )}
            {tc.admitido_at && tc.estado !== 'finalizada' && (
              <p className="text-xs text-estable">✓ Paciente admitido a las {new Date(tc.admitido_at).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' })}</p>
            )}
            {!tc.checkin_at && tc.estado !== 'finalizada' && (
              <p className="text-xs text-muted-foreground">El paciente aún no llega a la sala de espera.</p>
            )}

            {tc.estado !== 'finalizada' && (
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => setVideo(true)}><Video className="w-4 h-4 mr-1" /> Entrar a la sala de video</Button>
                <Button size="sm" variant="outline" isLoading={renovar.isPending} onClick={() => renovar.mutate()}>Renovar acceso</Button>
              </div>
            )}
            <p className="text-xs text-muted-foreground">Sala propia (video en nuestro servidor) · código {tc.codigo_acceso}{tc.acceso_expira ? ` · acceso hasta ${new Date(tc.acceso_expira).toLocaleString(LOCALE)}` : ''}</p>

            <div className="flex flex-wrap gap-2 border-t pt-2">
              {tc.estado !== 'en_curso' && tc.estado !== 'finalizada' && (
                <Button size="sm" isLoading={iniciar.isPending} disabled={!tc.consentimiento_aceptado}
                  title={tc.consentimiento_aceptado ? undefined : 'El paciente debe aceptar el consentimiento antes de iniciar'}
                  onClick={() => iniciar.mutate()}>Iniciar sesión</Button>
              )}
              {tc.estado === 'en_curso' && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setNota(true)}><FileSignature className="w-4 h-4 mr-1" /> Nota médica</Button>
                  <Button size="sm" variant="outline" onClick={() => setReceta(true)}><Pill className="w-4 h-4 mr-1" /> Receta</Button>
                  <Button size="sm" isLoading={finalizar.isPending} onClick={() => finalizar.mutate(calidad)}>Finalizar</Button>
                </>
              )}
            </div>
            {tc.estado === 'en_curso' && (
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Calidad de conexión">
                  <select className={fieldCls} value={calidad} onChange={(e) => { setCalidad(e.target.value); evento.mutate(e.target.value); }}>
                    <option value="buena">Buena</option><option value="regular">Regular</option><option value="mala">Mala</option>
                  </select>
                </Campo>
                <Campo label="Resultado (al finalizar)">
                  <select className={fieldCls} value={resultado} onChange={(e) => setResultado(e.target.value)}>
                    <option value="efectiva">Efectiva</option><option value="parcial">Parcial</option><option value="fallida">Fallida</option>
                  </select>
                </Campo>
              </div>
            )}
            {tc.estado === 'finalizada' && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Duración: {tc.duracion_seg ? `${Math.round(tc.duracion_seg / 60)} min` : '—'}{tc.calidad ? ` · calidad ${tc.calidad}` : ''}{tc.resultado ? ` · ${tc.resultado}` : ''}</p>
                <Button size="sm" variant="outline" onClick={() => setEncuesta(true)}><Star className="w-3.5 h-3.5 mr-1" /> Calificar la sesión</Button>
              </div>
            )}
          </div>

          {/* Columna derecha: chat */}
          <div className="border rounded-md flex flex-col h-96">
            <div className="px-3 py-2 border-b text-sm font-medium">Chat</div>
            <ChatTele teleconsultaId={tc.id} />
          </div>
        </div>
      )}

      {encuesta && tc && <EncuestaTele teleconsultaId={tc.id} titulo="¿Cómo estuvo la sesión? (percepción del médico)" onListo={() => setEncuesta(false)} />}
      {nota && data && <NuevaEvolucion pacienteId={data.paciente_id} onClose={() => setNota(false)} onSaved={() => setNota(false)} />}
      {receta && data && <NuevaReceta pacienteId={data.paciente_id} onClose={() => setReceta(false)} onSaved={() => setReceta(false)} />}
    </Modal>
  );
}

// ---- Chat reutilizable (médico y paciente) ----
export function ChatTele({ teleconsultaId }: { teleconsultaId: string }) {
  const qc = useQueryClient();
  const { data: mensajes = [] } = useQuery<TeleMensaje[]>({
    queryKey: ['med-tele-msj', teleconsultaId],
    queryFn: () => medRpc<TeleMensaje[]>('med_tele_mensajes', { _id: teleconsultaId }),
    refetchInterval: 4000,
  });
  const [texto, setTexto] = useState('');
  const finRef = useRef<HTMLDivElement>(null);
  useEffect(() => { finRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes.length]);
  const enviar = useMutation({
    mutationFn: () => medRpc('med_tele_enviar_mensaje', { _id: teleconsultaId, _texto: texto }),
    onSuccess: () => { setTexto(''); void qc.invalidateQueries({ queryKey: ['med-tele-msj', teleconsultaId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <>
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {mensajes.length === 0 && <p className="text-center text-xs text-muted-foreground py-4">Sin mensajes</p>}
        {mensajes.map((m) => (
          <div key={m.id} className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-sm ${m.emisor === 'paciente' ? 'bg-muted mr-auto' : 'bg-primary/10 ml-auto'}`}>
            {m.texto}
            {m.archivo_url && <a href={m.archivo_url} target="_blank" rel="noopener noreferrer" className="block text-xs text-primary underline">{m.archivo_nombre || 'archivo'}</a>}
            <div className="text-[10px] text-muted-foreground text-right">{new Date(m.created_at).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' })}</div>
          </div>
        ))}
        <div ref={finRef} />
      </div>
      <form className="flex gap-1 border-t p-2" onSubmit={(e) => { e.preventDefault(); if (texto.trim()) enviar.mutate(); }}>
        <input className={`${fieldCls} h-9`} placeholder="Escribe un mensaje…" value={texto} onChange={(e) => setTexto(e.target.value)} />
        <Button type="submit" size="sm" isLoading={enviar.isPending} disabled={!texto.trim()}><Send className="w-4 h-4" /></Button>
      </form>
    </>
  );
}

// ---- Modal de consentimiento (usado por el portal del paciente) ----
export function ConsentimientoTele({ teleconsultaId, onClose, onAceptado }: { teleconsultaId: string; onClose: () => void; onAceptado: () => void }) {
  const [acepta, setAcepta] = useState(false);
  const [grabacion, setGrabacion] = useState(false);
  const aceptar = useMutation({
    mutationFn: () => medRpc('med_tele_aceptar_consentimiento', { _id: teleconsultaId, _items: CONSENT_ITEMS, _grabacion: grabacion }),
    onSuccess: () => { toast.success('Consentimiento registrado'); onAceptado(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Modal title="Consentimiento informado · Telemedicina" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <ul className="list-disc pl-5 space-y-1">{CONSENT_ITEMS.map((c, i) => <li key={i}>{c}</li>)}</ul>
        <label className="flex items-center gap-2"><input type="checkbox" checked={grabacion} onChange={(e) => setGrabacion(e.target.checked)} /> Autorizo la grabación de la sesión.</label>
        <label className="flex items-center gap-2 font-medium"><input type="checkbox" checked={acepta} onChange={(e) => setAcepta(e.target.checked)} /> He leído y acepto lo anterior.</label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!acepta} isLoading={aceptar.isPending} onClick={() => aceptar.mutate()}>Aceptar y continuar</Button>
        </div>
      </div>
    </Modal>
  );
}
